import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Shield } from "lucide-react";
import { toast } from "sonner";

export default function AdminRolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPerms, setShowPerms] = useState(null);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [selectedPerms, setSelectedPerms] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/admin/roles"), api.get("/admin/permissions")]);
      setRoles(r.data || []);
      setPermissions(p.data || []);
    } catch { /* ignore */ }
  };

  const handleCreateRole = async () => {
    try {
      await api.post("/admin/roles", newRole);
      toast.success("Role created");
      setShowCreate(false);
      setNewRole({ name: "", description: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openPermissions = (role) => {
    setShowPerms(role);
    setSelectedPerms(role.permission_ids || []);
  };

  const togglePerm = (permId) => {
    setSelectedPerms((prev) => prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]);
  };

  const savePermissions = async () => {
    try {
      await api.put(`/admin/roles/${showPerms.id}/permissions`, { permission_ids: selectedPerms });
      toast.success("Permissions updated");
      setShowPerms(null);
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const modules = [...new Set(permissions.map((p) => p.module))];

  return (
    <div data-testid="admin-roles-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Roles & Permissions</h1>
        <Button data-testid="create-role-button" onClick={() => setShowCreate(true)} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">
          <Plus size={16} className="mr-1.5" /> New Role
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="border border-gray-200 shadow-sm rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={18} className="text-[#0052CC]" />
                <h3 className="text-sm font-bold">{role.name}</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">{role.description || "No description"}</p>
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-[10px] rounded-sm">{role.user_count || 0} users</Badge>
                <Button variant="outline" size="sm" onClick={() => openPermissions(role)} className="rounded-sm text-xs h-7">
                  Permissions ({role.permission_ids?.length || 0})
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-sm"><DialogHeader><DialogTitle>New Role</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Name</Label><Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label><Input value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Cancel</Button><Button onClick={handleCreateRole} className="bg-[#0052CC] rounded-sm">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showPerms} onOpenChange={() => setShowPerms(null)}>
        <DialogContent className="max-w-2xl rounded-sm"><DialogHeader><DialogTitle>Permissions for {showPerms?.name}</DialogTitle></DialogHeader>
          <div className="py-2 max-h-[400px] overflow-y-auto">
            {modules.map((mod) => (
              <div key={mod} className="mb-4">
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2 capitalize">{mod}</h4>
                <div className="flex flex-wrap gap-3">
                  {permissions.filter((p) => p.module === mod).map((p) => (
                    <label key={p.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox checked={selectedPerms.includes(p.id)} onCheckedChange={() => togglePerm(p.id)} />
                      <span className="capitalize">{p.action}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPerms(null)} className="rounded-sm">Cancel</Button><Button onClick={savePermissions} className="bg-[#0052CC] rounded-sm">Save Permissions</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
